import {
  createErrorNotifier,
  createInstance,
  createLogger,
  createStaticProjectConfigManager,
  createBatchEventProcessor,
  ERROR,
} from '@optimizely/optimizely-sdk';
import _isObject from 'lodash/isObject';

import { reportError, reportWarning } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';

const CACHE = new Map();

// These methods are for the feature flag implementation of Optimizely using the SDK.
const getSdkKey = () => (typeof window !== 'undefined' ? window.OPTIMIZELY_SDK_KEY : '');

let optimizelyInstance = null;
let optimizelyInitFailed = false;

async function getOptimizelyInstance() {
  if (optimizelyInitFailed) {
    return null;
  }

  if (!optimizelyInstance) {
    const sdkKey = getSdkKey();

    if (!sdkKey.length) {
      return null;
    }

    let datafileJSON;
    try {
      const datafile = await httpClient.get(`https://cdn.optimizely.com/datafiles/${sdkKey}.json`, {
        ignoreUnauthorizedError: true,
        ignoreForbiddenError: true,
      });
      datafileJSON = datafile?.data;
    } catch (error) {
      // Network errors are often caused by ad blockers so we report as warning
      optimizelyInitFailed = true;
      reportWarning('Optimizely datafile fetch failed', {
        errorCode: error?.code,
        errorMessage: error?.message,
      });
      return null;
    }

    // The log level can be set to DEBUG to see granular details on feature-enabled checks, etc.
    const logger = createLogger({
      level: ERROR,
    });

    const errorNotifier = createErrorNotifier({
      handleError: error => {
        reportError('Optimizely error', {
          error,
        });
      },
    });

    const projectConfigManager = createStaticProjectConfigManager({
      datafile: datafileJSON,
    });

    optimizelyInstance = createInstance({
      projectConfigManager,
      errorNotifier,
      logger,
      eventProcessor: createBatchEventProcessor({
        eventBatchSize: 10,
        eventFlushInterval: 1000,
      }),
    });
  }
  return optimizelyInstance;
}

async function getOptimizelyUser(userId, userAttributes) {
  await getOptimizelyInstance();
  await optimizelyInstance?.onReady();
  const userContext = optimizelyInstance?.createUserContext(userId, userAttributes);
  return userContext;
}

export async function isFeatureEnabledForUser(feature, userId, attributes = {}) {
  const uniqueKey = `${feature}-${userId}-${JSON.stringify(attributes)}`;
  const fromCache = CACHE.get(uniqueKey);

  if (fromCache) {
    return fromCache;
  }

  try {
    const userIdString = userId.toString();
    const userAttributes = {
      user_id: userIdString,
      ...(_isObject(attributes) ? attributes : {}),
    };

    const optimizelyUser = await getOptimizelyUser(userIdString, userAttributes);
    const decision = optimizelyUser?.decide(feature);

    const value = decision?.enabled || false;
    CACHE.set(uniqueKey, value);

    return value;
  } catch (error) {
    reportError('Error checking Optimizely feature flag:', {
      feature,
      userId,
      error,
    });
    return false;
  }
}
